import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDialog } from "@repo/ui";
import { configurationApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";

export const useUpgradeWorkflow = () => {
  const queryClient = useQueryClient();
  const { closeDialog, showDialog } = useDialog();

  const upgradeWorkflowMutation = useMutation({
    mutationFn: async (oldWorkflowId: number) => {
      const response = await configurationApi.client.POST("/api/Workflow/{oldWorkflowId}/upgrade", {
        params: { path: { oldWorkflowId } },
        parseAs: "text",
      });

      if (response?.error) {
        const error = getErrorMessage(response.error, "Failed to upgrade workflow");
        throw new Error(error);
      }

      if (response?.response?.status < 200 || response?.response?.status > 299) {
        throw new Error("Unexpected response status: " + response.response.status);
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: configurationApi.queryOptions("get", "/api/Workflow").queryKey,
      });
    },
  });

  const confirmAndUpgradeWorkflow = (workflowId: number) => {
    upgradeWorkflowMutation.reset();

    showDialog({
      title: "Confirm Workflow Upgrade",
      description: "Are you sure you want to upgrade this workflow? This action cannot be undone.",
      confirmText: "Upgrade",
      cancelVariant: "outline",
      cancelText: "Go Back",
      onConfirm: async () => {
        try {
          await upgradeWorkflowMutation.mutateAsync(workflowId);
          closeDialog();
        } catch (err) {
          console.error("Error upgrading workflow:", err);
        }
      },
    });
  };

  return {
    upgradeWorkflow: confirmAndUpgradeWorkflow,
    isUpgrading: upgradeWorkflowMutation.isPending,
    isSuccess: upgradeWorkflowMutation.isSuccess,
    error: upgradeWorkflowMutation.error,
    reset: upgradeWorkflowMutation.reset,
  };
};
